import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location} from '@angular/common';
import { ErrorNavigationService } from 'src/app/core/service/error-navigation.service';

type ErrorCode = '400' | '401' | '403' | '404' | '408' | '429' | '500' | '502' | '503' | '504' | 'default';

interface ErrorViewModel {
  code: string;
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  toneClass: string;
  primaryActionLabel: string;
  showRetry: boolean;
  showBack: boolean;
}

@Component({
  selector: 'app-error-page',
  templateUrl: './error-page.component.html',
  styleUrls: ['./error-page.component.scss'],
})
export class ErrorPageComponent implements OnInit {
  error: ErrorViewModel = {
    code: '500',
    title: 'Something went wrong',
    subtitle: 'We could not complete your request.',
    description: 'Please try again. If the issue continues, contact support.',
    icon: 'bi-exclamation-triangle-fill',
    toneClass: 'error-tone-server',
    primaryActionLabel: 'Retry',
    showRetry: true,
    showBack: true,
  };

  
  returnUrl = '/';
  showDetails = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly location: Location,
    private readonly errorNavigationService: ErrorNavigationService
  ) {}

  ngOnInit(): void {
    const code = this.normalizeCode(this.route.snapshot.paramMap.get('code'));
    const query = this.route.snapshot.queryParamMap;

    this.returnUrl = query.get('returnUrl') || '/';

    this.error = this.getErrorModel(code);

    // Special case: session timeout
    if (code === '401') {
      this.returnUrl = query.get('returnUrl') || '/';
    }
  }

onRetry(): void {

  const failedUrl =
    this.errorNavigationService.getFailedUrl();

  const retryCount =
    this.errorNavigationService.incrementRetryCount();

  if (retryCount > 1) {

    this.errorNavigationService.clear();

    this.router.navigate(['/']);

    return;
  }

  if (
    failedUrl &&
    !failedUrl.startsWith('/error')
  ) {

    this.router.navigateByUrl(failedUrl);

    return;
  }

  this.router.navigate(['/']);
}

goBack(): void {

  const failedUrl =
    this.errorNavigationService.getFailedUrl();

  if (
    failedUrl &&
    !failedUrl.startsWith('/error')
  ) {

    this.router.navigate(['/']);

    return;
  }

  this.location.back();
}



  private normalizeCode(value: string | null): ErrorCode {
    const allowed: ErrorCode[] = ['400', '401', '403', '404', '408', '429', '500', '502', '503', '504'];
    return (value && allowed.includes(value as ErrorCode) ? (value as ErrorCode) : 'default');
  }

  private getErrorModel(code: ErrorCode): ErrorViewModel {
    switch (code) {
      case '400':
        return {
          code: '400',
          title: 'Invalid Request',
          subtitle: 'We could not process the request.',
          description: 'Please review the input and try again.',
          icon: 'bi-x-circle-fill',
          toneClass: 'error-tone-client',
          primaryActionLabel: 'Go Back',
          showRetry: false,
          showBack: true,
        };

      case '401':
        return {
          code: '401',
          title: 'Session expired',
          subtitle: 'Please sign in again.',
          description: 'Your session is no longer valid or you are not authenticated.',
          icon: 'bi-person-lock',
          toneClass: 'error-tone-auth',
          primaryActionLabel: 'Sign in',
          showRetry: false,
          showBack: false,
        };

      case '403':
        return {
          code: '403',
          title: 'Access denied',
          subtitle: 'You do not have permission to view this resource.',
          description: 'If you believe this is an error, contact your administrator.',
          icon: 'bi-shield-lock-fill',
          toneClass: 'error-tone-authz',
          primaryActionLabel: 'Go to Dashboard',
          showRetry: false,
          showBack: true,
        };

      case '404':
        return {
          code: '404',
          title: 'Page not found',
          subtitle: 'The page you requested does not exist or has moved.',
          description: 'Check the URL or return to the dashboard.',
          icon: 'bi-search',
          toneClass: 'error-tone-not-found',
          primaryActionLabel: 'Go to Dashboard',
          showRetry: false,
          showBack: true,
        };

      case '408':
        return {
          code: '408',
          title: 'Request timed out',
          subtitle: 'The server took too long to respond.',
          description: 'Please try again.',
          icon: 'bi-clock-history',
          toneClass: 'error-tone-warning',
          primaryActionLabel: 'Retry',
          showRetry: true,
          showBack: true,
        };

      case '429':
        return {
          code: '429',
          title: 'Too many requests',
          subtitle: 'Please wait a moment and try again.',
          description: 'You have sent too many requests in a short period.',
          icon: 'bi-speedometer2',
          toneClass: 'error-tone-warning',
          primaryActionLabel: 'Retry',
          showRetry: true,
          showBack: true,
        };

      case '502':
        return {
          code: '502',
          title: 'Bad gateway',
          subtitle: 'A downstream service returned an invalid response.',
          description: 'This is usually temporary. Please try again shortly.',
          icon: 'bi-cloud-slash-fill',
          toneClass: 'error-tone-server',
          primaryActionLabel: 'Retry',
          showRetry: true,
          showBack: true,
        };

      case '503':
        return {
          code: '503',
          title: 'Service unavailable',
          subtitle: 'The service is temporarily unavailable.',
          description: 'Scheduled maintenance or a temporary outage may be in progress.',
          icon: 'bi-tools',
          toneClass: 'error-tone-maintenance',
          primaryActionLabel: 'Retry',
          showRetry: true,
          showBack: false,
        };

      case '504':
        return {
          code: '504',
          title: 'Gateway timeout',
          subtitle: 'The request timed out while waiting for a response.',
          description: 'Please retry after a few moments.',
          icon: 'bi-hourglass-split',
          toneClass: 'error-tone-warning',
          primaryActionLabel: 'Retry',
          showRetry: true,
          showBack: true,
        };

      case '500':
      default:
        return {
          code: '500',
          title: 'Something went wrong',
          subtitle: 'We could not complete your request.',
          description: 'Please try again. If the issue persists, contact support.',
          icon: 'bi-exclamation-triangle-fill',
          toneClass: 'error-tone-server',
          primaryActionLabel: 'Retry',
          showRetry: true,
          showBack: true,
        };
    }
  }
}